import { PatientMedicationOverviewCard } from '@ctw/shared/content/medications/patient-medications-overview-card';
import { usePatientMedicationsOverviewData } from '@ctw/shared/content/medications/use-patient-medications-overview-data';
import { withOverviewCardErrorBoundary } from '@ctw/shared/content/overview/with-overview-card-error-boundary';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useWritebacks } from '@ctw/shared/context/writeback-provider';
import { useZapContext } from '@ctw/shared/context/zap-context';
import type { SpecificResourceOverviewCardProps } from '../overview/resource-overview-card';
import { useMedicationRowActions } from './patient-medications';

const TITLE = 'Medications';

export const PatientMedicationOverview = withOverviewCardErrorBoundary({
  cardTitle: TITLE,
  boundaryName: 'PatientMedicationOverview',
  Component: ({ onSeeAllResources }: SpecificResourceOverviewCardProps) => {
    const { isWritebackEnabled } = useWritebacks();
    const { onZapEvent } = useZapContext();
    const rowActions = useMedicationRowActions((record) => {
      void onZapEvent({
        name: 'addToRecord',
        payload: {
          resource: record.resource,
          resourceType: 'MedicationStatement',
        },
      });
    });

    const { openResourceDrawer } = useResourceDrawer({
      resourceActions: isWritebackEnabled('medications-all') ? (rowActions as never) : undefined,
      enableReadActions: false,
    });

    const { data, isLoading, hasNoRecords } = usePatientMedicationsOverviewData();

    return (
      <PatientMedicationOverviewCard
        onRowClick={({ telemetry, record }) => openResourceDrawer({ telemetry, model: record })}
        data={data}
        isLoading={isLoading}
        emptyState={
          hasNoRecords
            ? "We didn't find any medication records for this patient."
            : 'There are no medications with prescriptions or fills within the past 6 months for this patient.'
        }
        footerCTA={
          // Keep the CTA visible while loading (chrome renders with a body spinner)
          // and whenever there are records; only hide it once we know there are none.
          isLoading || !hasNoRecords
            ? {
                label: 'All Medications',
                onClick: onSeeAllResources,
              }
            : undefined
        }
      />
    );
  },
});
